import type { AntibodyValidationMetadata } from '../generated/generatedOpenAPI';

const DEFAULT_EXPECTED_EXTENSION = '.csv';
const DEFAULT_MINIMUM_FILE_ROWS = 20;
const DEFAULT_SEQUENCE_LENGTH_THRESHOLD = 200;
const EXTRA_HEADER_COLUMNS_WARNING = 'Extra header columns provided.';
const DUPLICATE_HEADER_COLUMNS_WARNING = 'Duplicate columns provided';

type ValidationSeverity = 'error' | 'warning' | 'info';

export interface ValidationFormattingOptions {
  expectedFileExtension?: string;
  minimumFileRows?: number;
  sequenceLengthThreshold?: number;
}

export interface ValidationMessage {
  severity: ValidationSeverity;
  message: string;
}

export interface ValidationMessageBundle {
  detailed: ValidationMessage[];
  short: ValidationMessage[];
}

/**
 * Convert antibody validation metadata returned from the backend into user-facing messages.
 * Consumers can display either the `short` summary messages, the detailed list, or both.
 */

function collectValidationIssues(metadata: AntibodyValidationMetadata, options: ValidationFormattingOptions): ValidationMessage[] {
  const {
    expectedFileExtension = DEFAULT_EXPECTED_EXTENSION,
    minimumFileRows = DEFAULT_MINIMUM_FILE_ROWS,
    sequenceLengthThreshold = DEFAULT_SEQUENCE_LENGTH_THRESHOLD,
  } = options;

  const messages: ValidationMessage[] = [];
  const { fileExtension, fileRows, missingColumns, headerParsingError, rowParsingErrors } = metadata;

  if (fileExtension && fileExtension.toLowerCase() !== expectedFileExtension.toLowerCase()) {
    messages.push({
      severity: 'error',
      message: 'Incompatible file format. Please use a 10x Cell Ranger CSV.',
    });
  }

  if (typeof fileRows === 'number') {
    if (fileRows <= 0) {
      messages.push({ severity: 'error', message: 'No valid rows found. Please use a 10x Cell Ranger CSV.' });
    } else if (fileRows < minimumFileRows) {
      messages.push({
        severity: 'warning',
        message: `Low number of valid rows. Results may lack statistical significance.`,
      });
    }
  }

  let missingHeader = false;
  if (Array.isArray(missingColumns) && missingColumns.length > 0) {
    if (missingColumns.length < 3) {
      // If there are only a small number of missing columns, we specify which ones are missing to help users debug their file formatting issues.
      messages.push({
        severity: 'error',
        message: `Incompatible file format, missing column(s): ${missingColumns.join(', ')}. Please use a 10x Cell Ranger CSV.`,
      });
    }
    if (missingColumns.length >= 3 && missingColumns.length <= 20) {
      // If there are a moderate number of missing columns, we avoid listing them all in the error message to prevent overwhelming users, but we still want to flag it as an error.
      messages.push({
        severity: 'error',
        message: `Incompatible file format, missing ${missingColumns.length} columns. Please use a 10x Cell Ranger CSV.`,
      });
    }
    if (missingColumns.length > 20) {
      // If there are a large number of missing columns, it's likely that the entire header is missing.
      // We flag it as missing here, so that we avoid false duplicate detection in the next block.
      missingHeader = true;
    }
  }

  if (headerParsingError) {
    if (headerParsingError === EXTRA_HEADER_COLUMNS_WARNING) {
      // This condition is currently returned by the backend when extra columns are present in the header, but all required columns are detected and valid.
      // We want to allow uploads to succeed in this case.
      // messages.push({ severity: 'warning', message: 'Extra columns detected.' });
    } else if (headerParsingError.includes(DUPLICATE_HEADER_COLUMNS_WARNING) && !missingHeader) {
      messages.push({ severity: 'error', message: 'Incompatible file format. Duplicate header column(s) detected. Please use a 10x Cell Ranger CSV.' });
    } else {
      messages.push({ severity: 'error', message: 'Incompatible file format. Missing header row. Please use a 10x Cell Ranger CSV.' });
    }
  }

  // No need to check for row parsing errors because if there are row parsing errors,
  // the backend will return an empty fileRows count and we will show a "No valid rows found" error.
  // This prevents overwhelming users with a long list of row parsing errors that
  // may be caused by the same underlying issue in the file format.
  // if (Array.isArray(rowParsingErrors) && rowParsingErrors.length > 0) {
  //   messages.push({
  //     severity: 'error',
  //     message: 'Incompatible file format. Row parsing errors detected.',
  //   });
  // }

  if (!metadata.lightChainObserved) {
    messages.push({ severity: 'warning', message: 'No light chain rows detected.' });
  }

  if (!metadata.heavyChainObserved) {
    messages.push({ severity: 'warning', message: 'No heavy chain rows detected.' });
  }

  if (typeof metadata.maxSequenceLength === 'number' && metadata.maxSequenceLength < sequenceLengthThreshold) {
    messages.push({
      severity: 'warning',
      message: `All chain sequences are shorter than ${sequenceLengthThreshold} residues (max observed: ${metadata.maxSequenceLength}).`,
    });
  }

  return messages;
}

function buildShortMessages(issues: ValidationMessage[]): ValidationMessage[] {
  const errors = issues.filter((issue) => issue.severity === 'error');
  const warnings = issues.filter((issue) => issue.severity === 'warning');

  if (errors.length > 0) {
    const headline = errors.length === 1 && errors[0] ? errors[0].message : `${errors.length} validation error(s) detected. Resolve before continuing.`;
    return [{ severity: 'error', message: headline }];
  }

  if (warnings.length > 0) {
    const headline =
      warnings.length === 1 && warnings[0] ? warnings[0].message : `${warnings.length} validation warning(s) detected. Review before continuing.`;
    return [{ severity: 'warning', message: headline }];
  }

  return [{ severity: 'info', message: 'File validation completed successfully.' }];
}

export function formatAntibodyValidationMessages(metadata: AntibodyValidationMetadata, options: ValidationFormattingOptions = {}): ValidationMessageBundle {
  const issues = collectValidationIssues(metadata, options);
  const shortMessages = buildShortMessages(issues);

  return {
    detailed: issues,
    short: shortMessages,
  };
}
